<template>
  <div
    :id="`${source}_message_actions`"
    class="flex flex-col"
    @mouseenter="$emit('mouse-enter')"
    @mouseleave="$emit('mouse-leave')"
  >
    <div
      id="message_action_options"
      class="flex flex-row items-center p-1 rounded-lg shadow-lg bg_color border_8"
    >
      <template
        v-for="(action, index) in actionDetails"
        :key="`${action?.id}_message_actions_${index}`"
      >
        <a-dropdown
          v-if="action.id === messageActions.MORE_ACTIONS"
          :trigger="['click']"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          @visibleChange="handleDropdownVisibleChange"
        >
          <ActionWrapper
            source="message_actions"
            :title="action?.title"
            @click.stop="action?.handler?.()"
          >
            <ThemeImage
              :style="{
                width: `${action?.size}px`,
                height: `${action?.size}px`,
              }"
              :icon="action?.icon"
              :alt="`${action?.id}_icon`"
            />
          </ActionWrapper>

          <template #overlay>
            <a-menu class="min-w-[168px]">
              <a-menu-item
                v-for="(option, optIndex) in action.options"
                :key="`${option.id}_option_${optIndex}`"
                @click="option?.handler?.()"
              >
                <div class="flex flex-row items-center gap-2">
                  <ThemeImage
                    :style="{
                      width: `${option?.size}px`,
                      height: `${option?.size}px`,
                    }"
                    :icon="option?.icon"
                    :alt="`${option?.id}_icon`"
                  />
                  <span class="text-sm">{{ option.title }}</span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>

        <EmojiPicker
          v-else-if="action.id === messageActions.ADD_REACTION"
          ref="emojiPickerRef"
          v-model="selectedEmoji"
          placement="bottom-end"
          @picker-open="handleDropdownVisibleChange(true)"
          @picker-close="handleDropdownVisibleChange(false)"
          @picker-mouseenter="$emit('mouse-enter')"
          @picker-mouseleave="$emit('mouse-leave')"
        >
          <ActionWrapper source="message_actions" :title="action?.title">
            <ThemeImage
              :style="{
                width: `${action?.size}px`,
                height: `${action?.size}px`,
              }"
              :icon="action?.icon"
              :alt="`${action?.id}_icon`"
            />
          </ActionWrapper>
        </EmojiPicker>

        <ActionWrapper
          v-else
          source="message_actions"
          :title="action?.title"
          @click.stop="action?.handler?.()"
        >
          <ThemeImage
            :style="{ width: `${action?.size}px`, height: `${action?.size}px` }"
            :icon="action?.icon"
            :alt="`${action?.id}_icon`"
          />
        </ActionWrapper>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { message } from "ant-design-vue";
import { copyToClipboard } from "@/src/utils/utils.js";
import {
  getMessageActionDetails,
  messageActions,
} from "@/src/constants/chatConstants.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import ChatService from "@/src/api/ChatService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import EmojiPicker from "@/src/components/custom_components/EmojiPicker.vue";
import ActionWrapper from "@/src/components/custom_components/ActionWrapper.vue";

const props = defineProps({
  source: { type: String, required: true },
  id: { type: [String, Number], required: true },
  threadId: { type: String, default: null },
  isBroadcastMessage: { type: Boolean, default: false },
  messageContent: { type: String, default: "" },
  messageSenderId: { type: Number, default: null },
  messageAttachments: { type: Array, default: () => [] },
  chat: { type: Object, default: null },
});

const emit = defineEmits([
  "dropdown-open",
  "dropdown-close",
  "mouse-enter",
  "mouse-leave",
  "reaction-updated",
  "edit-message",
  "open-thread",
]);

const selectedEmoji = ref("");
const hasOpenModal = ref(false);
const emojiPickerRef = ref(null);

const messageID = computed(() => props.id);
const activeChat = computed(() => props.chat || store.getters["chats/activeChat"]);
const currentUserId = computed(() => {
  const userId = localStorage.getItem("userId");
  return userId ? Number(userId) : null;
});
const canDeleteMessage = computed(() => {
  if (!props.messageSenderId || !currentUserId.value) {
    return false;
  }
  return currentUserId.value === props.messageSenderId;
});
const hasTasksProject = computed(() => {
  const isV1 = store.getters["appStore/isAppStoreV1"];
  
  if (isV1) {
    return true;
  }
  
  const projects = store.getters["projects/projects"] || [];
  return projects.length > 0;
});
const isPendingReaction = (emoji) => {
  const key = `${messageID.value}:${emoji}`;
  return store.getters["chats/isPendingReaction"](key);
};

const closeEmojiPicker = () => {
  const picker = emojiPickerRef.value?.[0] || emojiPickerRef.value;
  if (picker?.showPicker) {
    picker.togglePicker();
  }
};

const handleDropdownVisibleChange = (visible) => {
  try {
    emit(visible ? "dropdown-open" : "dropdown-close");
  } catch (error) {
    console.error(
      "An error occurred while handling dropdown visible change: ",
      error,
    );
  }
};

const handleAddReaction = async () => {
  try {
    if (!selectedEmoji.value) {
      return;
    }

    if (!activeChat.value?.id) {
      message.warning("No active chat selected");
      selectedEmoji.value = "";
      return;
    }

    const emoji = selectedEmoji.value;
    const pendingKey = `${messageID.value}:${emoji}`;

    if (isPendingReaction(emoji)) {
      selectedEmoji.value = "";
      return;
    }

    selectedEmoji.value = "";

    await store.dispatch("chats/addPendingReaction", {
      key: pendingKey,
      action: "toggle",
    });

    const response = await ChatService.addReaction(
      messageID.value,
      activeChat.value.id,
      emoji,
    );

    if (response?.success) {
      // 201 = reaction added, 200 = reaction removed (toggle)
      const wasAdded = response.status === 201;
      const delta = wasAdded ? 1 : -1;

      if (props.source === "embedded_chat" || props.source === "embedded_thread") {
        store.commit("embeddedChats/UPDATE_REACTION", {
          messageId: messageID.value,
          emoji: emoji,
          delta: delta,
        });
      } else if (props.source === "threads") {
        await store.dispatch("chats/updateThreadMessageReactions", {
          messageId: messageID.value,
          emojiKey: emoji,
          delta: delta,
        });
      } else {
        await store.dispatch("chats/updateMessageReactions", {
          messageId: messageID.value,
          emoji: emoji,
          increment: delta,
        });
      }
      emit("reaction-updated", messageID.value);
    } else {
      message.error(response?.error || "Failed to update reaction");
    }
    await store.dispatch("chats/removePendingReaction", pendingKey);
  } catch (error) {
    const emoji = selectedEmoji.value;
    if (emoji) {
      const pendingKey = `${messageID.value}:${emoji}`;
      await store.dispatch("chats/removePendingReaction", pendingKey);
    }
    console.error("An error occurred while toggling reaction: ", error);
    message.error("Failed to update reaction!");
    selectedEmoji.value = "";
  }
};

const handleCreateTask = () => {
  try {
    closeEmojiPicker();

    hasOpenModal.value = true;
    handleDropdownVisibleChange(true);
    eventBus.emit("create-task", {
      messageId: messageID.value,
      messageContent: props.messageContent,
      messageSenderId: props.messageSenderId,
      source: props.source,
    });
  } catch (error) {
    console.error("An error occurred while handling create task: ", error);
  }
};

const handleModalClosed = (closedMessageId) => {
  try {
    if (closedMessageId === messageID.value && hasOpenModal.value) {
      hasOpenModal.value = false;
      handleDropdownVisibleChange(false);
    }
  } catch (error) {
    console.error("An error occurred while handling modal closed: ", error);
  }
};

const handleOpenThread = async () => {
  try {
    closeEmojiPicker();

    if (props.source === "embedded_chat") {
      emit("open-thread", { messageId: messageID.value, threadId: props.threadId || messageID.value });
      return;
    }

    await store.dispatch("chats/toggleThread", {
      messageID: messageID.value,
      threadId: props.threadId || messageID.value,
      forceState: true,
    });
  } catch (error) {
    console.error("An error occurred while trying to open thread: ", error);
  }
};

const handleEditMessage = () => {
  try {
    closeEmojiPicker();

    if (!activeChat.value) {
      message.warning("No active chat selected");
      return;
    }

    if (props.source === "embedded_chat" || props.source === "embedded_thread") {
      emit("edit-message", {
        id: messageID.value,
        groupId: activeChat.value.id,
        text: props.messageContent,
        source: props.source,
      });
      return;
    }

    store.dispatch("chats/setEditingMessage", {
      id: messageID.value,
      groupId: activeChat.value.id,
      text: props.messageContent,
      source: props.source,
    });
  } catch (error) {
    console.error("An error occurred while trying to edit the message: ", error);
  }
};

const handleDeleteMessage = () => {
  try {
    closeEmojiPicker();

    if (!activeChat.value) {
      message.warning("No active chat selected");
      return;
    }

    hasOpenModal.value = true;
    handleDropdownVisibleChange(true);

    let deleteThreadId = props.threadId || messageID.value;

    if (props.isBroadcastMessage) {
      deleteThreadId = messageID.value;
    }

    const messageDetails = {
      messageId: messageID.value,
      recipientType: activeChat.value.recipient_type,
      recipientId: activeChat.value.id,
      threadId: deleteThreadId,
    };

    eventBus.emit("delete-message", messageDetails);
  } catch (error) {
    console.error(
      "An error occurred while trying to delete the message: ",
      error,
    );
  }
};

const handleCopyMessage = () => {
  try {
    closeEmojiPicker();

    if (!props.messageContent || !props.messageContent.trim()) {
      message.warning("No message to copy");
      return;
    }

    copyToClipboard(props.messageContent);
    handleDropdownVisibleChange(false);
  } catch (error) {
    console.error("Failed to copy message: ", error);
  }
};

const handleForwardMessage = () => {
  try {
    closeEmojiPicker();

    hasOpenModal.value = true;
    emit("dropdown-open");

    eventBus.emit("forward-message", {
      messageId: messageID.value,
      messageContent: props.messageContent,
      messageSenderId: props.messageSenderId,
      messageAttachments: props.messageAttachments || [],
    });
  } catch (error) {
    console.error("Failed to forward message: ", error);
  }
};

const handlers = {
  handleAddReaction,
  handleCreateTask,
  handleOpenThread,
  handleEditMessage,
  handleDeleteMessage,
  handleCopyMessage,
  handleForwardMessage,
};

const actionDetails = computed(() =>
  getMessageActionDetails(handlers, props.source, canDeleteMessage.value, hasTasksProject.value, props.isBroadcastMessage),
);

watch(selectedEmoji, (newVal) => {
  if (newVal) {
    handleAddReaction();
  }
});

onMounted(() => {
  eventBus.on("task-modal-closed", handleModalClosed);
  eventBus.on("delete-modal-closed", handleModalClosed);
  eventBus.on("forward-modal-closed", handleModalClosed);
  
  const isV1 = store.getters["appStore/isAppStoreV1"];
  if (!isV1) {
    const projects = store.getters["projects/projects"] || [];
    if (projects.length === 0) {
      store.dispatch("projects/fetchProjects");
    }
  }
});

onUnmounted(() => {
  eventBus.off("task-modal-closed", handleModalClosed);
  eventBus.off("delete-modal-closed", handleModalClosed);
  eventBus.off("forward-modal-closed", handleModalClosed);
});
</script>
